import {
  CardComponent,
  CardCVV,
  CardExpiry,
  CardNumber,
} from "@chargebee/chargebee-js-react-wrapper";
import * as Sentry from "@sentry/react";
import { useRef, useState } from "react";
import { toast } from "react-toastify";

import PoweredByStripe from "common/components/PoweredByStripe";
import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import usePaymentSources from "common/datahooks/usePaymentSources";
import { HTTPError } from "common/helpers/HTTP";

import type ChargebeeComponents from "@chargebee/chargebee-js-react-wrapper/dist/components/ComponentGroup";

interface CreditCardProps {
  onBack: () => void;
  onTokenReceived: (token: string) => void;
}

export default function CreditCard({
  onTokenReceived,
  onBack,
}: CreditCardProps) {
  const cardRef = useRef<ChargebeeComponents>();

  const [isParsingCard, setIsParsingCard] = useState(false);
  const [nameOnCard, setNameOnCard] = useState("");

  const [errorsState, setErrorsState] = useState({
    number: null,
    expiry: null,
    cvv: null,
  });

  const {
    isAddingPaymentSource,
    addPaymentSourceError,
    resetAddPaymentSourceError,
  } = usePaymentSources();

  async function getToken(): Promise<string> {
    const { token } = await cardRef.current.tokenize({
      firstName: nameOnCard.trim(),
    });

    return token;
  }

  async function onCreditCardSave() {
    let token: string;

    try {
      resetAddPaymentSourceError();
      setIsParsingCard(true);
      token = await getToken();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error adding card";

      toast.error(errorMessage);

      Sentry.setExtra("error", error);
      Sentry.captureMessage(errorMessage);
    } finally {
      setIsParsingCard(false);
    }

    onTokenReceived(token);
  }

  function onChange(state: ChargebeeCurrentState) {
    const { cardType, error, empty: isEmpty } = state;

    setErrorsState((prevState) => ({
      ...prevState,
      [state.field]: error?.message,
    }));

    if (cardType === "amex" && !isEmpty) {
      setErrorsState((prevState) => ({
        ...prevState,
        number: "AMEX is not supported",
      }));
    }
  }

  const isLoading = isParsingCard || isAddingPaymentSource;

  return (
    <>
      <FormField
        label="Card holder's name"
        htmlFor="cardHolder"
        className="mb-4"
      >
        <Input
          id="cardHolder"
          placeholder="Example: John Doe"
          value={nameOnCard}
          onChange={(e) => setNameOnCard(e.target.value)}
          onBlur={(e) => {
            setErrorsState((prevState) => ({
              ...prevState,
              name: e.target.value.trim()
                ? null
                : "Card holder's name is required",
            }));
          }}
        />
      </FormField>

      {/* Note: Known issue in Chargebee (Strict Mode extra iframe) -> https://github.com/chargebee/chargebee-js-wrappers/issues/98 */}
      <CardComponent
        ref={cardRef}
        classes={{ invalid: "invalid-card-input" }}
        fonts={[{ fontFamily: "", src: "" }]}
        // @ts-expect-error: the chargebee type definitions are wrong
        onChange={onChange}
      >
        <div className="flex flex-col gap-4">
          <FormField
            label="Card number"
            htmlFor="cardNumber"
            error={errorsState.number}
          >
            <CardNumber
              className="chargebee-input-field"
              placeholder="Card number"
            />
          </FormField>

          <div className="flex flex-row justify-start gap-4">
            <FormField
              label="Expiry date"
              htmlFor="expiryDate"
              error={errorsState.expiry}
              className="w-1/2"
            >
              <CardExpiry className="chargebee-input-field" />
            </FormField>
            <FormField
              label="CVC"
              htmlFor="cvc"
              error={errorsState.cvv}
              className="w-1/2"
            >
              <CardCVV className="chargebee-input-field" />
            </FormField>
          </div>
        </div>
      </CardComponent>
      <PoweredByStripe />

      {addPaymentSourceError && (
        <span className="mt-3 text-center text-red-500">
          {(addPaymentSourceError instanceof HTTPError &&
            addPaymentSourceError.serverMessage) ||
            "Failed to add card. Please try again or contact support"}
        </span>
      )}

      <div className="mt-10 flex justify-between items-center gap-x-2.5">
        <Button onClick={onBack} variant="secondary-black" disabled={isLoading}>
          Back
        </Button>

        <Button
          isLoading={isLoading}
          onClick={onCreditCardSave}
          disabled={!nameOnCard.trim()}
        >
          Add card
        </Button>
      </div>
    </>
  );
}
